import { ZipLookup } from '@utility/zipLookup.js';
import { updateCart } from '@utility/shopify-cart.js';

function parseISODate(str) {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export class ShippingEstimate extends HTMLElement {
  // GrowthBook flag `shipping-estimate` controls how this renders:
  //   control  - show nothing
  //   zip      - full zip-code based estimate
  //   national - estimate based on the national average, no zip input
  // Anything else (flag missing, still loading, unrecognized value) is treated as control
  // and shows nothing. This is a deliberately cautious default during testing; remove once
  // the flag is fully rolled out and give `zip` back its old default status if wanted.
  connectedCallback() {
    const gb = window.growth_book;
    if (gb && gb.ready) {
      this._start(gb.getFeatureValue('shipping-estimate'));
    } else {
      window.addEventListener(
        'growthbook:ready',
        () => this._start(window.growth_book.getFeatureValue('shipping-estimate')),
        { once: true }
      );
    }
  }

  _start(mode) {
    if (mode !== 'zip' && mode !== 'national') {
      this.replaceChildren();
      this.style.display = 'none';
      return;
    }
    this._nationalMode = mode === 'national';

    this.template = this.querySelector(this._nationalMode ? '#shipping-estimate-template-national' : '#shipping-estimate-template');
    const dataScript = this.querySelector('#shipping-estimate-data');
    const cartDataScript = this.querySelector('#shipping-estimate-cart-data');
    if (!this.template || !dataScript || !cartDataScript) return;

    let data, cartItems;
    try {
      data = JSON.parse(dataScript.textContent);
      cartItems = JSON.parse(cartDataScript.textContent);
    } catch (e) {
      console.error('shipping-estimate: could not parse shipping estimate data', e);
      return;
    }

    // Freight-only feature: if nothing in the cart is freight, there's nothing to estimate.
    const freightItems = cartItems.filter((item) => item.shipping_type === 'Freight');

    if (freightItems.length === 0) {
      this.replaceChildren();
      this.style.display = 'none';
      return;
    }

    this._freightShipDates = freightItems
      .filter((item) => item.estimated_ship_date)
      .map((item) => parseISODate(item.estimated_ship_date));

    this._holidays = (data.holidays || []).map(parseISODate);
    this._handlingTimeHours = Number(data.handling_time) || 0;
    // Entries ending in "*" are prefix matches (e.g. "832*" disables all 832xx zips); everything
    // else must match the entered zip exactly.
    this._disabledZips = (data.disabled_zips || []).map((zip) => String(zip).trim()).filter(Boolean);
    this._country = this.dataset.country || 'US';
    this._uszipsUrl = this.dataset.uszipsUrl;

    // Only US is supported for now.
    if (this._country !== 'US') {
      this.replaceChildren();
      this.style.display = 'none';
      return;
    }

    const transitUrl = data.freight_transit_data?.url;
    if (!transitUrl) return;

    // Build the DOM once, the zip input then just triggers recomputation, not a rebuild.
    const clone = this.template.content.cloneNode(true);
    this.replaceChildren(clone);
    this._zipInput = this.querySelector('.zip-input');
    this._minEl = this.querySelector('.min-date');
    this._maxEl = this.querySelector('.max-date');
    this._contentEl = this.querySelector('.content');
    this._deliveryWindowEl = this.querySelector('.delivery-window');
    this._zipErrorEl = this.querySelector('.text-error');

    // National mode has no zip input in the template, so there's nothing to wire up: skip
    // localStorage lookup, the input listener, and location/geolocation resolution entirely.
    if (!this._nationalMode) {
      // shipping_zip is only ever written when the user directly edits the zip input (below), so finding
      // it here means they've already told us their zip. Skip location/geolocation resolution entirely.
      const savedZip = window.localStorage && window.localStorage.getItem('shipping_zip');
      if (this._zipInput) this._zipInput.value = savedZip || this.dataset.defaultZip || '';

      let zipEditedByUser = false;
      this._zipInput?.addEventListener('input', () => {
        zipEditedByUser = true;
        clearTimeout(this._zipDebounce);
        this._zipDebounce = setTimeout(() => {
          const zip = this._zipInput.value.trim();
          if (window.localStorage) {
            if (zip) {
              window.localStorage.setItem('shipping_zip', zip);
            } else {
              window.localStorage.removeItem('shipping_zip');
            }
          }
          this._renderEstimate();
        }, 300);
      });

      if (!savedZip) {
        console.log('[shipping-estimate] no saved zip, attempting to resolve from location');
        this._resolveLocationZip()
          .then((zip) => {
            console.log('[shipping-estimate] resolved zip from location:', zip, 'zipEditedByUser:', zipEditedByUser);
            // this.dataset.defaultZip is already in the input; only overwrite it if we found something
            // better and the user hasn't started typing their own zip in the meantime.
            if (zip && !zipEditedByUser && this._zipInput) {
              this._zipInput.value = zip;
              this._renderEstimate();
            }
          })
          .catch((e) => console.error('[shipping-estimate] could not resolve zip from location', e));
      }
    }

    fetch(transitUrl)
      .then((response) => response.json())
      .then((transitTree) => {
        this._transitTree = transitTree;
        this._renderEstimate();
      })
      .catch((e) => console.error('shipping-estimate: could not load transit data', e));
  }

  // Resolves a zip from the dealer locator's cached location (reverse-geocoding it via uszips.json
  // if it doesn't already have one), or from IP-based geolocation (same service the dealer locator
  // uses, no permission prompt) if there's no cached location at all. Returns null if localStorage
  // isn't available or every source comes up empty.
  async _resolveLocationZip() {
    if (!window.localStorage) return null;

    const location = JSON.parse(window.localStorage.getItem('location'));
    console.log('[shipping-estimate] cached location:', location);

    if (location) {
      if (location.postal_code) return location.postal_code;

      const zip = await this._zipFromLatLng(location.lat, location.lng);
      if (zip) {
        location.postal_code = zip;
        window.localStorage.setItem('location', JSON.stringify(location));
      }
      return zip;
    }

    const apiKey = window.themeVariables?.settings?.ipGeolocationApiKey;
    console.log('[shipping-estimate] no cached location, ipGeolocationApiKey present:', !!apiKey);
    if (!apiKey) return null;

    const response = await fetch(`https://api.ipgeolocation.io/ipgeo?apiKey=${apiKey}`);
    console.log('[shipping-estimate] ipgeolocation.io status:', response.status, response.ok);
    if (!response.ok) return null;
    const ipLocation = await response.json();

    const lat = ipLocation.latitude;
    const lng = ipLocation.longitude;
    const zip = await this._zipFromLatLng(lat, lng);
    if (zip) {
      window.localStorage.setItem(
        'location',
        JSON.stringify({ lat, lng, state: ipLocation.state_code?.split('-')[1], country: ipLocation.country_code2, postal_code: zip })
      );
    }
    return zip;
  }

  // Lazily loads uszips.json (only once, and only if we actually need to reverse-geocode) then finds
  // the nearest zip to the given coordinates.
  async _zipFromLatLng(lat, lng) {
    if (lat == null || lng == null || !this._uszipsUrl) return null;

    if (!this._zipLookup) {
      this._zipLookup = new ZipLookup();
      await this._zipLookup.load(this._uszipsUrl);
    }

    return this._zipLookup.nearest(lat, lng)?.zip || null;
  }

  _findTransitNode(zip) {
    const minDataPoints = this._transitTree.meta?.min_data_points ?? 0;
    const countryNode = this._transitTree.tree?.nodes?.find((node) => node.id === this._country);
    if (!countryNode) return null;

    const normalizedZip = (zip || '').toUpperCase().replace(/[^0-9A-Z]/g, '');
    const segments = [normalizedZip.slice(0, 1), normalizedZip.slice(0, 2), normalizedZip.slice(0, 3), normalizedZip.slice(0, 5)];

    const path = [countryNode];
    let current = countryNode;
    for (const segment of segments) {
      if (!segment) break;
      const next = current.nodes?.find((node) => node.id === segment);
      if (!next) break;
      path.push(next);
      current = next;
    }

    // Walk back from the most specific match to the first node with enough data to trust.
    for (let i = path.length - 1; i >= 0; i--) {
      if (path[i].n >= minDataPoints) return path[i];
    }

    return countryNode;
  }

  _isZipDisabled(zip) {
    if (!zip) return false;
    return this._disabledZips.some((pattern) =>
      pattern.endsWith('*') ? zip.startsWith(pattern.slice(0, -1)) : zip === pattern
    );
  }

  _renderEstimate() {
    if (!this._transitTree) return;

    const zip = this._zipInput?.value?.trim() || '';

    if (!this._nationalMode && this._isZipDisabled(zip)) {
      if (this._deliveryWindowEl) this._deliveryWindowEl.hidden = true;
      if (this._zipErrorEl) this._zipErrorEl.hidden = false;
      this._contentEl?.removeAttribute('data-tooltip');
      this._saveCartAttributes({
        _shipping_estimate_min: '',
        _shipping_estimate_max: '',
        _shipping_estimate_zip: zip,
        _shipping_estimate_info: 'Zip code not eligible for freight shipping.',
      });
      return;
    }
    if (this._deliveryWindowEl) this._deliveryWindowEl.hidden = false;
    if (this._zipErrorEl) this._zipErrorEl.hidden = true;

    const node = this._findTransitNode(zip);
    if (!node) return;

    const handlingDays = Math.ceil(this._handlingTimeHours / 24);
    const normalHandlingDate = this._addBusinessDays(new Date(), handlingDays);

    const maxShipDate = this._freightShipDates.length
      ? new Date(Math.max(...this._freightShipDates))
      : null;
    const useShipDate = !!(maxShipDate && maxShipDate > normalHandlingDate);
    const baseDate = useShipDate ? maxShipDate : normalHandlingDate;

    const lowDays = Math.ceil(node.low);
    const highDays = Math.ceil(node.high);
    const minDate = this._addBusinessDays(baseDate, lowDays);
    const maxDate = this._addBusinessDays(baseDate, highDays);

    if (this._minEl) this._minEl.textContent = this._formatDate(minDate);
    if (this._maxEl) this._maxEl.textContent = this._formatDate(maxDate);

    const zipLabel = node.id === this._country ? 'United States' : node.id.padEnd(5, '-');
    const holidaysInWindow = this._holidays
      .filter((holiday) => holiday >= new Date() && holiday <= maxDate)
      .sort((a, b) => a - b);
    const holidaysText = holidaysInWindow.length
      ? ` (DECKED will not be shipping on ${holidaysInWindow.map((d) => this._formatDate(d)).join(', ')})`
      : '';

    const tooltipText = `This information is based on ${node.n} deliveries to ${zipLabel} over the past 12 months. Order will ship ${this._formatDate(baseDate)}${holidaysText} and take ${lowDays}-${highDays} business days in transit.`;

    this._contentEl?.setAttribute('data-tooltip', tooltipText);

    this._saveCartAttributes({
      _shipping_estimate_min: this._formatISODate(minDate),
      _shipping_estimate_max: this._formatISODate(maxDate),
      _shipping_estimate_zip: zip,
      _shipping_estimate_info: tooltipText,
    });
  }

  // Persists the estimate shown to the customer as cart attributes, so it's visible on the order later.
  // Skips the request if nothing has actually changed since the last save. _shipping_estimate_updated_at
  // is stamped fresh only when there's a real change to save, so it reflects when the estimate itself
  // last changed, not just whenever this happens to run.
  _saveCartAttributes(attributes) {
    const attributesKey = JSON.stringify(attributes);
    if (attributesKey === this._lastSavedCartAttributes) {
      return;
    }
    this._lastSavedCartAttributes = attributesKey;

    const attributesWithTimestamp = { ...attributes, _shipping_estimate_updated_at: new Date().toISOString() };
    updateCart({ attributes: attributesWithTimestamp }).catch((e) =>
      console.error('[shipping-estimate] could not save cart attributes', e.response?.status, e.response?.data || e)
    );
  }

  _isWeekend(date) {
    const day = date.getDay();
    return day === 0 || day === 6;
  }

  _isHoliday(date) {
    return this._holidays.some((holiday) => isSameDay(holiday, date));
  }

  // Advances baseDate by the given number of business days, skipping weekends and holidays.
  _addBusinessDays(baseDate, businessDays) {
    let date = new Date(baseDate);
    let remaining = businessDays;

    while (remaining > 0) {
      date = addDays(date, 1);
      if (!this._isWeekend(date) && !this._isHoliday(date)) {
        remaining--;
      }
    }

    return date;
  }

  _formatDate(date) {
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  }

  // YYYY-MM-DD from local date parts (not toISOString, which converts to UTC and can shift the day).
  _formatISODate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
